import { useQuery } from '@tanstack/react-query';
import { api } from './api';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * THE ANNOUNCEMENT FEED, FETCHED ONCE
 *
 * This module exists because of a real crash.
 *
 * Two pages each called `useQuery` with the key `['announcements', 'feed']`
 * and returned DIFFERENT shapes from it — the Announcements page returned the
 * whole `{ items, meta }` envelope, the dashboard returned `items` alone. A
 * React Query cache entry belongs to its key, not to the component that wrote
 * it, so whichever resolved last decided what BOTH components read.
 *
 * Visiting Announcements and then the dashboard therefore handed the dashboard
 * an object where it expected an array, and it died on `data.slice(...)`. A
 * hard refresh looked like a fix only because the dashboard then mounted
 * first and wrote the shape it wanted.
 *
 * The two also passed different pagination params under that one key, so even
 * the request was ambiguous.
 *
 * One key, one request, one shape, in one place. A second caller cannot
 * disagree with the first about what the key holds, because there is only one
 * caller now.
 * ═══════════════════════════════════════════════════════════════════════════
 */

export interface AnnouncementItem {
  id: string;
  title: string;
  body: string;
  priority: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
  /** Signed, time-limited URL for the attached image, or null. */
  mediaUrl: string | null;
  mediaType: string | null;
  publishedAt: string | null;
  expiresAt: string | null;
  isRead: boolean;
  createdBy: { firstName: string; lastName: string };
}

export interface AnnouncementFeed {
  items: AnnouncementItem[];
  meta: { unread: number };
}

export type AnnouncementPriority = AnnouncementItem['priority'];

/**
 * How a priority looks — one definition, read by the feed and the dashboard.
 *
 * Only the two that need acting on are TINTED. A page where every card is
 * coloured has no colour left to raise an alarm with: if the compliance
 * deadline and the new-course notice both arrive on a wash, the wash stops
 * meaning anything. Normal and Low keep a plain card and say what they are
 * with the rail alone.
 *
 * The rail is always priority, never read-state. Priority is a permanent fact
 * about the message; unread is a transient one, and it already has the "New"
 * badge — using the same stripe for both meant an urgent notice lost its red
 * the moment somebody glanced at it.
 */
export function priorityStyles(priority: AnnouncementPriority): string {
  switch (priority) {
    case 'URGENT':
      return 'border-l-destructive bg-destructive/[0.06]';
    case 'HIGH':
      return 'border-l-warning bg-warning/[0.07]';
    case 'NORMAL':
      return 'border-l-brand-700';
    case 'LOW':
      return 'border-l-muted-foreground/30';
  }
}

export const announcementKeys = {
  /** Everything announcement-shaped, for blanket invalidation. */
  all: () => ['announcements'] as const,
  feed: () => ['announcements', 'feed'] as const,
  authored: () => ['announcements', 'authored'] as const,
  /** Deleted, awaiting restore or destruction. */
  trash: () => ['announcements', 'trash'] as const,
};

/**
 * The signed-in person's announcements.
 *
 * One page of 50 serves both readers: the Announcements screen lists them all
 * and the dashboard shows the newest three, so a second request would fetch
 * the same rows twice.
 */
export function useAnnouncementFeed() {
  return useQuery({
    queryKey: announcementKeys.feed(),
    queryFn: async () => {
      const { data } = await api.get<AnnouncementFeed>('/announcements/feed', {
        params: { page: 1, pageSize: 50 },
      });
      return data;
    },
  });
}
